import type { ReactNode } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { CardTableArea } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui/Table';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import { publicUrl } from '@/lib/env';
import { formatDateTime } from '@/lib/format';
import type { Product } from '@/types/products';
import {
  describeFaq,
  describeOrderVariant,
  formatNumber,
  formatTierQuantity,
  inventorySummary,
  photoLabel,
  sortedPriceTiers,
  summarizeProductExtras,
  supportedProductLabel,
  variantPhotoRows,
  variantStockRows,
} from './products-format';
import { useProduct } from './use-products';

/**
 * Ficha de solo lectura de un producto: exactamente lo que el bot tiene de ese artículo.
 *
 * El detalle viene de `GET .../products/:productId` (hook `useProduct`) y, mientras llega, se pinta
 * la fila que ya estaba en la tabla: así el diálogo abre al instante y se completa solo. Es la
 * pantalla donde se comprueba "por qué el bot dijo eso": precios por cantidad, variantes, reglas,
 * FAQs, fotos y anuncios, todos juntos.
 */

export interface ProductDetailDialogProps {
  open: boolean;
  onClose: () => void;
  /** Producto seleccionado en la tabla (`null` = diálogo cerrado). */
  product: Product | null;
  /** Abre el formulario de edición con este producto (solo si el usuario es ADMIN). */
  onEdit?: (product: Product) => void;
}

/** Bloque de la ficha: título + contenido. */
function DetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2 border-t border-line pt-4">
      <h3 className="text-micro font-semibold tracking-wide text-ink-faint uppercase">{title}</h3>
      {children}
    </section>
  );
}

/** Lista con viñetas (reglas, variantes) o texto de relleno si no hay nada. */
function BulletList({ items, empty }: { items: string[]; empty: string }) {
  if (items.length === 0) return <p className="text-caption text-ink-faint">{empty}</p>;

  return (
    <ul className="list-disc space-y-1 pl-4 text-caption text-ink-soft">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

export function ProductDetailDialog({ open, onClose, product, onEdit }: ProductDetailDialogProps) {
  const { tenantId, canEdit } = useActiveTenantAccess();

  /**
   * El detalle fresco del endpoint. Mientras llega (o si falla) se usa la fila de la tabla, que ya
   * tiene todo menos las fechas: el diálogo nunca aparece vacío.
   */
  const detailQuery = useProduct(tenantId, product?.id ?? null);
  const detail = detailQuery.data ?? product;

  if (!detail) return null;

  const tiers = sortedPriceTiers(detail.tablaPrecios);
  // Inventario por combinación (21-sep-2026): un producto de talla × color no tiene UN stock.
  const variantRows = variantStockRows(detail.inventarioVariantes);
  // Fotos por color/variante (21-sep-2026): qué le manda el bot al cliente cuando pide fotos.
  const variantPhotos = variantPhotoRows(detail.fotosVariantes);
  const inventory = inventorySummary(detail);
  const variantes = detail.datosParaOrden ?? [];
  const reglas = detail.reglas ?? [];
  const faqs = detail.faqs ?? [];
  const extras = summarizeProductExtras(detail);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={detail.nombre}
      description={
        extras.length > 0
          ? extras.join(' · ')
          : 'Sin fotos, variantes ni reglas: el bot solo tiene el nombre y el precio.'
      }
      footer={
        <>
          {onEdit && canEdit ? (
            <Button variant="secondary" onClick={() => onEdit(detail)}>
              Editar
            </Button>
          ) : null}
          <Button onClick={onClose}>Cerrar</Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={detail.activo ? 'success' : 'neutral'} dot>
            {detail.activo ? 'Activo' : 'Inactivo'}
          </Badge>

          {detail.inventario === null && variantRows.length === 0 ? (
            <Badge tone="neutral" dot>
              Sin control de inventario
            </Badge>
          ) : inventory.soldOut ? (
            <Badge tone="danger" dot>
              Agotado
            </Badge>
          ) : (
            <Badge tone="info" dot>
              {variantRows.length > 0 ? inventory.label : `${inventory.label} disponibles`}
            </Badge>
          )}

          {detail.metaAdIds.length > 0 ? (
            <Badge tone="brand">
              {supportedProductLabel(detail.metaAdIds.length, 'anuncio vinculado', 'anuncios vinculados')}
            </Badge>
          ) : null}
        </div>

        {detailQuery.isError ? <Alert>{getErrorMessage(detailQuery.error)}</Alert> : null}

        {variantRows.length > 0 ? (
          <DetailSection title="Inventario por variante">
            <CardTableArea>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Combinación</TableHeaderCell>
                    <TableHeaderCell align="right">Unidades</TableHeaderCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {variantRows.map((fila) => (
                    <TableRow key={fila.etiqueta}>
                      <TableCell>{fila.etiqueta}</TableCell>
                      <TableCell align="right" className="tabular font-medium text-ink">
                        {fila.cantidad === 0 ? 'Agotada' : formatNumber(fila.cantidad)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardTableArea>
          </DetailSection>
        ) : null}

        <DetailSection title="Tabla de precios">
          {tiers.length === 0 ? (
            <p className="text-caption text-ink-faint">
              Sin rangos de precio: el backend no deja guardar un producto así.
            </p>
          ) : (
            <CardTableArea>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Cantidad</TableHeaderCell>
                    <TableHeaderCell align="right">Precio unitario</TableHeaderCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {tiers.map((tier) => (
                    <TableRow key={tier.cantidad}>
                      <TableCell>{formatTierQuantity(tier.cantidad)}</TableCell>
                      <TableCell align="right" className="tabular font-medium text-ink">
                        {formatNumber(tier.precio)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardTableArea>
          )}
        </DetailSection>

        <DetailSection title="Descripción">
          {detail.descripcion ? (
            <p className="text-caption whitespace-pre-line text-ink-soft">{detail.descripcion}</p>
          ) : (
            <p className="text-caption text-ink-faint">Sin descripción.</p>
          )}
        </DetailSection>

        <DetailSection title="Datos para la orden">
          <BulletList
            items={variantes.map(describeOrderVariant)}
            empty="Sin variantes: el bot solo pregunta la cantidad."
          />
        </DetailSection>

        <DetailSection title="Reglas del producto">
          <BulletList items={reglas} empty="Sin reglas especiales." />
        </DetailSection>

        <DetailSection title="Preguntas frecuentes">
          {faqs.length === 0 ? (
            <p className="text-caption text-ink-faint">Sin preguntas frecuentes.</p>
          ) : (
            <dl className="space-y-2">
              {faqs.map((faq) => (
                <div
                  key={describeFaq(faq)}
                  className="rounded-panel border border-line bg-surface-muted/40 px-3 py-2"
                >
                  <dt className="text-caption font-medium text-ink">{faq.pregunta}</dt>
                  <dd className="text-caption whitespace-pre-line text-ink-muted">{faq.respuesta}</dd>
                </div>
              ))}
            </dl>
          )}
        </DetailSection>

        <DetailSection title="Fotos">
          {detail.fotos.length === 0 ? (
            <p className="text-caption text-ink-faint">
              Sin fotos: el bot no podrá reconocer el producto si el cliente manda una imagen.
            </p>
          ) : (
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {detail.fotos.map((foto) => (
                <li key={foto}>
                  <a
                    href={publicUrl(foto)}
                    target="_blank"
                    rel="noreferrer"
                    title={foto}
                    className="block space-y-1"
                  >
                    <img
                      src={publicUrl(foto)}
                      alt={photoLabel(foto)}
                      loading="lazy"
                      className="h-24 w-full rounded-control border border-line object-cover"
                    />
                    <span className="block truncate text-micro text-brand-600 hover:underline">
                      {photoLabel(foto)}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </DetailSection>

        <DetailSection title="Fotos por color / variante">
          {variantPhotos.length === 0 ? (
            <p className="text-caption text-ink-faint">
              Sin fotos por color: cuando el cliente pida fotos, el bot manda las generales de arriba.
            </p>
          ) : (
            <ul className="space-y-3">
              {variantPhotos.map((grupo) => (
                <li key={grupo.etiqueta} className="space-y-1">
                  <p className="text-caption font-medium text-ink-soft">{grupo.etiqueta}</p>

                  <ul className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                    {grupo.fotos.map((foto) => (
                      <li key={foto}>
                        <a
                          href={publicUrl(foto)}
                          target="_blank"
                          rel="noreferrer"
                          title={foto}
                          className="block space-y-1"
                        >
                          <img
                            src={publicUrl(foto)}
                            alt={`Foto de ${grupo.etiqueta}`}
                            loading="lazy"
                            className="h-20 w-full rounded-control border border-line object-cover"
                          />
                          <span className="block truncate text-micro text-brand-600 hover:underline">
                            {photoLabel(foto)}
                          </span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </DetailSection>

        <DetailSection title="Anuncios de Meta">
          <BulletList
            items={detail.metaAdIds}
            empty="Sin anuncios vinculados: las ventas de este producto no se pueden atribuir a un anuncio."
          />
        </DetailSection>

        {/* Vínculo con Dropi (docs/DROPI.md): solo lectura. El stock es el del PROVEEDOR y lo refresca
            la sincronización automática (`dropi.stock.ts`): con 0 unidades el bot deja de ofrecer el
            producto, así que es el número que el admin necesita ver. */}
        {detail.dropiProductId ? (
          <DetailSection title="Dropi">
            <dl className="space-y-2">
              <DropiFila label="Producto en Dropi" value={`#${detail.dropiProductId}`} />
              <DropiFila label="Proveedor" value={detail.dropiProveedor ?? 'Sin dato'} />
              <DropiFila label="Bodega de origen" value={describeBodegaDropi(detail)} />
              <DropiFila label="Stock en Dropi" value={describeStockDropi(detail)} />
              <DropiFila
                label="Costo del proveedor"
                value={
                  typeof detail.dropiCosto === 'number'
                    ? formatNumber(detail.dropiCosto)
                    : 'Sin dato'
                }
              />
              <DropiFila
                label="Última sincronización"
                value={
                  detail.dropiSyncAt ? formatDateTime(detail.dropiSyncAt) : 'Sin sincronizar todavía'
                }
              />
            </dl>
          </DetailSection>
        ) : null}

        <p className="text-micro text-ink-faint">
          Creado el {formatDateTime(detail.createdAt)} · Última edición:{' '}
          {formatDateTime(detail.updatedAt)}
        </p>
      </div>
    </Modal>
  );
}

/** Una fila de la sección Dropi: etiqueta a la izquierda, dato a la derecha. */
function DropiFila({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-right font-medium text-ink">{value}</dd>
    </div>
  );
}

/**
 * El stock del PROVEEDOR, con el aviso que importa: en 0 el bot ya no ofrece el producto (lo decide
 * `estaAgotado` con el número que dejó la última sincronización). `null` = todavía no se sincronizó.
 */
function describeStockDropi(product: Product): string {
  if (typeof product.dropiStock !== 'number') {
    return 'Sin dato: sincronizalo desde Integraciones → Dropi';
  }

  return product.dropiStock === 0
    ? '0 · agotado en Dropi, el bot ya no lo ofrece'
    : `${formatNumber(product.dropiStock)} unidades`;
}

/** «Denuevoo · CALI (76001000)» o el aviso de que la bodega todavía no se conoce. */
function describeBodegaDropi(product: Product): string {
  const bodega = product.dropiBodega;

  if (!bodega?.ciudad) return 'Sin dato: se completa al sincronizar una orden';

  const codDane = bodega.codDane ? ` (${bodega.codDane})` : '';
  return `${bodega.nombre ? `${bodega.nombre} · ` : ''}${bodega.ciudad}${codDane}`;
}
